<template>
  <div class="product-info-area">
    <div class="product-info-tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="product-info-tab"
        :class="{ 'is-active': tab.key === current }"
        role="tab"
        :id="`${idPrefix}-tab-${tab.key}`"
        :aria-selected="tab.key === current"
        :aria-controls="`${idPrefix}-panel-${tab.key}`"
        @click="select(tab.key)"
      >
        <p v-html="tab.label"></p>
        <span class="product-info-tab_arrow"></span>
      </button>
    </div>

    <div class="product-info-panels">
      <div
        v-for="tab in tabs"
        :key="tab.key"
        class="product-info-panel"
        :class="{ 'is-active': tab.key === current }"
        :id="`${idPrefix}-panel-${tab.key}`"
        role="tabpanel"
        :aria-labelledby="`${idPrefix}-tab-${tab.key}`"
        :hidden="tab.key !== current"
      >
        <h3 class="product-info-panel_title">{{ tab.panelTitle }}</h3>
        <slot :name="tab.key" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-info-area`（商品詳細の情報タブ）。タブ枠は 38/38。
 *
 * **本数は可変だが並びは固定。** 支給38本の実測は3パターンだけで、
 * 「商品特徴 → FAQ → 成分・商品情報」25本 ／「商品特徴 → FAQ → ご愛用者様レビュー →
 * 成分・商品情報」9本 ／「商品特徴 → 成分・商品情報」4本。
 * **`成分・商品情報` が常に最後**で、間の FAQ とレビューが増減する。
 *
 * したがって呼び出し側は `['feature','faq','reviews','spec']` の固定順から、
 * **商品別 const にキーがあるものだけ**を渡す（決定#58。静的マップは持たない）。
 *
 * 支給の JS（`script_main.js:356-372`）は `is-active` の付け替えと `hidden` の出し入れだけなので、
 * `ref` ＋ `computed` で置き換える。`role="tablist"` / `role="tab"` / `aria-selected` は支給が
 * 持っているものをそのまま出し、**支給に無い `role="tabpanel"` と `aria-controls` /
 * `aria-labelledby` はこちらで補う**（§3.6-3 のタブと同じ扱い）。
 *
 * ラベルに `<br class="tb-only">` `<br class="sp-only">` が入る（「ご愛用者様<br>レビュー」
 * 「成分・商品<br>情報」）ため、ここだけ `v-html` を使う。**支給の固定文言に限る**ので、
 * 商品別コンテンツを `v-html` で描かない方針（決定#56）とは衝突しない。
 * → CONVERSION-GUIDE.md §3.3.1 の #10
 */
type Tab = {
  /** `feature` / `faq` / `reviews` / `spec` */
  key: string;
  /** タブの文字。改行用の `<br class="tb-only">` を含むため HTML で受ける */
  label: string;
  /**
   * パネルの中の見出し（`product-info-panel_title`）。**タブの文字とは別物**で、
   * レビューはタブが「ご愛用者様レビュー」・見出しが「お客様レビュー」、
   * FAQ はタブが「FAQ」・見出しが「FAQ〈商品に関するよくあるご質問〉」。
   * 支給38本の全数集計では商品による揺れが無く4種に収束する
   * （商品特徴38 / 成分・商品情報38 / FAQ34 / お客様レビュー9）。
   */
  panelTitle: string;
};

type Props = {
  tabs: Tab[];
  idPrefix?: string;
};

const props = withDefaults(defineProps<Props>(), {
  idPrefix: 'product',
});

const current = ref<string>(props.tabs[0]?.key ?? '');

const select = (key: string): void => {
  current.value = key;
};
</script>
